import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Globe, Mic, Smartphone, Share, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLang } from "@/App";
import { Logo } from "@/components/Navbar";
import ParallaxLayer from "@/components/ParallaxLayer";

const SIDE_CHAR = "https://images.unsplash.com/photo-1620215175664-cb9a6f5b6103?q=80&w=900&auto=format&fit=crop";

// Detect any phone/tablet (Android, iPhone, iPad) — phones get only the install button
const isPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

// Detect if already installed as PWA
const isInstalled = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export default function AndroidSection() {
    const { t, lang } = useLang();
    const [installEvt, setInstallEvt] = useState(null);
    const [phone, setPhone] = useState(false);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        setPhone(isPhone());
        setInstalled(isInstalled());

        const onPrompt = (e) => {
            e.preventDefault();
            setInstallEvt(e);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        return () => window.removeEventListener("beforeinstallprompt", onPrompt);
    }, []);

    const install = async () => {
        if (!installEvt) return;
        installEvt.prompt();
        await installEvt.userChoice;
        setInstallEvt(null);
    };

    return (
        <section id="android" className="relative py-20 lg:py-28 overflow-hidden" data-testid="android-section">
            <ParallaxLayer
                speed={-65}
                testid="android-parallax-1"
                className="absolute left-[-40px] top-1/2 -translate-y-1/2 w-[380px] h-[540px] char-frame bg-cover bg-center opacity-[0.1] pointer-events-none"
                style={{ backgroundImage: `url(${SIDE_CHAR})` }}
            />
            <ParallaxLayer
                speed={75}
                testid="android-parallax-2"
                className="hidden lg:block absolute right-[-50px] top-[6%] w-[260px] h-[380px] char-bleed bg-cover bg-center opacity-[0.09] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1780702335071-06e6e65ba5f8?q=80&w=600&auto=format&fit=crop)" }}
            />
            <div className="absolute right-[-160px] top-1/3 w-[480px] h-[480px] rounded-full bg-[#ff4bd8]/10 blur-[130px] pointer-events-none" />
            <div className="kana kana-watermark absolute right-[38%] top-4 text-[6rem] lg:text-[9rem] leading-none pointer-events-none hidden md:block" aria-hidden="true">
                ドロイド
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 34 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="flex items-center gap-3">
                        <div className="font-mono2 text-[11px] tracking-[0.3em] edg-text">{t.android.kicker}</div>
                        <span className="warning-stripes w-16 h-2 opacity-80" />
                    </div>
                    <h2 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl lg:text-4xl mt-3">
                        {t.android.title}
                    </h2>
                    <p className="text-sm sm:text-base text-slate-400 mt-5 leading-relaxed max-w-lg">{t.android.desc}</p>

                    <div className="grid grid-cols-2 gap-3 mt-8" data-testid="android-features">
                        {t.android.features.map((f) => (
                            <div key={f.k} className="glow-card rounded-xl p-4">
                                <div className="font-mono2 text-[10px] tracking-[0.2em] text-[#49f5e8]">{f.k}</div>
                                <div className="text-xs text-slate-400 mt-1.5 leading-snug">{f.v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-4 mt-9">
                        {/* HERO: PWA install — one tap, zero warnings, works on any phone */}
                        {installEvt && !installed ? (
                            <button
                                data-testid="android-install-btn"
                                onClick={install}
                                className="flex items-center justify-center gap-3 bg-[#fcee0a] text-[#080a12] font-black text-lg px-10 py-5 rounded-2xl hover:shadow-[0_0_44px_rgba(252,238,10,0.65)] transition-shadow w-full sm:w-auto sm:min-w-[340px]"
                            >
                                <Smartphone size={24} />
                                {lang === "ua" ? "Встановити на телефон" : "Install on phone"}
                            </button>
                        ) : installed ? (
                            <div
                                data-testid="android-installed-badge"
                                className="flex items-center justify-center gap-3 bg-[#d7ff35]/20 text-[#d7ff35] font-black text-lg px-10 py-5 rounded-2xl border-2 border-[#d7ff35]/50 w-full sm:w-auto sm:min-w-[340px]"
                            >
                                <Smartphone size={24} />
                                {lang === "ua" ? "Встановлено ✓" : "Installed ✓"}
                            </div>
                        ) : (
                            <button
                                data-testid="android-install-btn"
                                onClick={() => {
                                    // Fallback for browsers without beforeinstallprompt (iOS Safari etc.)
                                    toast.info(lang === "ua"
                                        ? (isIOS() ? "Натисни «Поділитися» → «На початковий екран»" : "Натисни ⋮ → «Додати на головний екран»")
                                        : (isIOS() ? "Tap Share → \"Add to Home Screen\"" : "Tap ⋮ → \"Add to Home screen\""));
                                }}
                                className="flex items-center justify-center gap-3 bg-[#fcee0a] text-[#080a12] font-black text-lg px-10 py-5 rounded-2xl hover:shadow-[0_0_44px_rgba(252,238,10,0.65)] transition-shadow w-full sm:w-auto sm:min-w-[340px]"
                            >
                                <Smartphone size={24} />
                                {lang === "ua" ? "Встановити на телефон" : "Install on phone"}
                            </button>
                        )}
                        <p className="font-mono2 text-[10px] tracking-[0.2em] text-[#d7ff35]/80" data-testid="pwa-hero-note">
                            {lang === "ua" ? "✓ БЕЗ ПОПЕРЕДЖЕНЬ PLAY ЗАХИСТУ · 1 ТАП · ПРАЦЮЄ ОФЛАЙН" : "✓ NO PLAY PROTECT WARNINGS · 1 TAP · WORKS OFFLINE"}
                        </p>
                        {/* Direct APK download — secondary option on phone */}
                        {phone && (
                            <a
                                data-testid="android-apk-direct"
                                href="/downloads/KiberAI.apk"
                                download
                                className="flex items-center gap-2.5 border border-[#d7ff35]/50 text-[#d7ff35] font-mono2 text-xs tracking-[0.12em] uppercase px-6 py-3.5 rounded-full hover:bg-[#d7ff35]/10 transition-colors self-start"
                            >
                                <Download size={15} />
                                {lang === "ua" ? "Або APK-файл (6.7 MB)" : "Or APK file (6.7 MB)"}
                            </a>
                        )}
                    </div>
                    {phone && (
                        <div className="font-mono2 text-[10px] text-slate-500 leading-relaxed mt-4 max-w-md space-y-2" data-testid="apk-install-note">
                            <p>
                                {lang === "ua"
                                    ? "→ Рекомендовано: «Встановити на телефон» — без жодних попереджень, одразу працює"
                                    : "→ Recommended: \"Install on phone\" — no warnings at all, works instantly"}
                            </p>
                            <p>
                                {lang === "ua"
                                    ? "→ APK: якщо Play Захист блокує, натисни «Докладніше» → «Все одно встановити». Застосунок просить лише інтернет і підписаний ключем розробника"
                                    : "→ APK: if Play Protect blocks it, tap \"More details\" → \"Install anyway\". The app only requests internet and is signed by the developer's key"}
                            </p>
                        </div>
                    )}

                    {/* Secondary downloads — PC only, hidden on any phone */}
                    {!phone && (
                        <div className="flex flex-wrap gap-4 mt-4">
                            <a
                                data-testid="android-download-apk"
                                href="/downloads/CyberpunkCompanion-AndroidStudio.zip"
                                download
                                className="flex items-center gap-3 bg-[#d7ff35] text-[#080a12] font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_34px_rgba(215,255,53,0.55)] transition-shadow"
                            >
                                <Download size={16} />
                                {t.android.ctaApk}
                            </a>
                            <a
                                data-testid="android-download-web"
                                href="/downloads/Neon_AI_Website.zip"
                                download
                                className="flex items-center gap-3 px-6 py-3.5 rounded-full border border-[#49f5e8]/50 text-[#49f5e8] font-mono2 text-xs tracking-[0.15em] uppercase hover:bg-[#49f5e8]/10 transition-colors"
                            >
                                <Globe size={15} />
                                {t.android.ctaWeb}
                            </a>
                        </div>
                    )}

                    {/* APK card — PC only */}
                    {!phone && (
                        <div
                            className="mt-8 max-w-md rounded-2xl border-2 border-[#fcee0a]/70 bg-[#0b0e18]/90 p-5 shadow-[0_0_34px_rgba(252,238,10,0.12)]"
                            data-testid="apk-card"
                        >
                            <div className="flex items-center gap-3">
                                <Smartphone size={22} className="text-[#fcee0a]" />
                                <span className="font-display font-bold text-sm tracking-[0.15em] text-[#fcee0a]">{t.android.apk.title}</span>
                            </div>
                            <a
                                data-testid="apk-card-download"
                                href="/downloads/KiberAI.apk"
                                download
                                className="mt-4 flex items-center justify-center gap-2.5 border border-[#fcee0a] text-[#fcee0a] font-bold text-sm py-3 rounded-xl hover:bg-[#fcee0a]/10 transition-colors"
                            >
                                <Download size={16} />
                                {t.android.apk.btn}
                            </a>
                            <div className="mt-4 space-y-1.5 font-mono2 text-[10px] tracking-[0.15em] text-slate-400">
                                {t.android.apk.meta.map(([k, v]) => (
                                    <div key={k} className="flex justify-between">
                                        <span>{k}</span>
                                        <span className="text-slate-200">{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="font-mono2 text-[10px] text-[#fcee0a]/70 mt-4" data-testid="android-install-hint">
                        → {t.android.installHint}
                    </p>
                    <p className="font-mono2 text-[10px] text-slate-500 leading-relaxed mt-4 max-w-lg" data-testid="android-note">
                        ⚠ {t.android.note}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50, rotate: 2 }}
                    whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="flex justify-center"
                >
                    <motion.div
                        animate={{ y: [0, -12, 0] }}
                        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                        className="relative w-[270px] sm:w-[300px] rounded-[2.6rem] border border-[#2c3a5c] bg-[#0a0e1a] p-3 shadow-[0_0_80px_rgba(73,245,232,0.14)]"
                        data-testid="android-phone-mockup"
                    >
                        <div className="rounded-[2rem] border border-[#212b45] bg-[#080a12] overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-[#212b45]">
                                <div className="flex items-center gap-2">
                                    <Logo size={22} />
                                    <span className="font-display text-[10px] tracking-[0.2em] font-bold">KIBER AI</span>
                                </div>
                                <span className="font-mono2 text-[8px] text-[#49f5e8] flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-[#49f5e8] pulse-dot" />
                                    {t.android.phoneStatus}
                                </span>
                            </div>
                            <div className="warning-stripes h-1.5 opacity-80" />
                            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#212b45]" data-testid="phone-persona-row">
                                {["/char/av-lucy.png", "/char/av-david.png", "/char/av-rebecca.png", "/char/av-maine.png", "/char/av-kiwi.png"].map((a, i) => (
                                    <img
                                        key={a}
                                        src={a}
                                        alt="persona"
                                        className={`w-8 h-8 rounded-full object-cover border-2 ${
                                            i === 0 ? "border-[#fcee0a] shadow-[0_0_10px_rgba(252,238,10,0.5)]" : "border-[#2c3a5c] opacity-70"
                                        }`}
                                    />
                                ))}
                                <span className="kana text-[9px] text-slate-500 ml-auto">キャラ</span>
                            </div>
                            <div className="px-4 py-4 space-y-2.5 min-h-[300px]">
                                <div className="max-w-[85%] bg-[#131a2c] border border-[#26324e] rounded-xl rounded-tl-sm px-3 py-2 text-[10px] leading-relaxed text-slate-300">
                                    {lang === "ua" ? "Система на зв'язку. Що робимо, еджраннере?" : "System online. What's the move, edgerunner?"}
                                </div>
                                <div className="max-w-[85%] ml-auto bg-gradient-to-br from-[#3a1d63] to-[#5b21a0] border border-[#b56dff]/40 rounded-xl rounded-tr-sm px-3 py-2 text-[10px] leading-relaxed">
                                    {lang === "ua" ? "Гей, Kiber — зателефонуй мамі" : "Hey Kiber — call mom"}
                                </div>
                                <div className="max-w-[85%] bg-[#131a2c] border border-[#26324e] rounded-xl rounded-tl-sm px-3 py-2 text-[10px] leading-relaxed text-slate-300">
                                    {lang === "ua" ? "Набираю: «Мама» 📞 З'єднання..." : "Dialing: “Mom” 📞 Connecting..."}
                                </div>
                                <div className="max-w-[85%] ml-auto bg-gradient-to-br from-[#3a1d63] to-[#5b21a0] border border-[#b56dff]/40 rounded-xl rounded-tr-sm px-3 py-2 text-[10px] leading-relaxed">
                                    {lang === "ua" ? "Відкрий карти" : "Open maps"}
                                </div>
                            </div>
                            <div className="flex items-center gap-2.5 px-4 py-3.5 border-t border-[#212b45]">
                                <div className="w-8 h-8 rounded-full bg-[#d7ff35] grid place-items-center text-[#080a12]">
                                    <Mic size={14} />
                                </div>
                                <div className="flex items-end gap-1 h-5">
                                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                                        <span key={i} className="eq-bar w-[3px] h-4 bg-[#d7ff35]/80 rounded-sm" style={{ animationDelay: `${i * 0.1}s` }} />
                                    ))}
                                </div>
                                <span className="font-mono2 text-[8px] tracking-[0.25em] text-slate-500 ml-auto">
                                    {lang === "ua" ? "СЛУХАЮ..." : "LISTENING..."}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
